import { useConnection } from '@/features/connections/connection-context';
import { useSubcollections } from '@/query/firebase';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FirestoreDocument } from '../../types';
import { formatFieldValue, updateUrlParams } from '../../utils';
import {
  getDocumentFieldEntries,
  getSubcollectionDocLabel,
  hasTimestamps,
  type PeekFieldType,
} from './document-inspector.domain';

export interface DocumentInspectorProps {
  document: FirestoreDocument | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onViewJson: () => void;
}

const COPY_FEEDBACK_MS = 2000;

export function useDocumentInspector({
  document,
  open,
  onOpenChange,
  onViewJson,
}: DocumentInspectorProps) {
  const [copied, setCopied] = useState(false);
  const [peekValue, setPeekValue] = useState<{
    value: unknown;
    fieldName: string;
    fieldType: PeekFieldType;
  } | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { connectionId } = useConnection();

  const documentPath = document?.path ?? '';
  const { data: subcollectionsData } = useSubcollections(connectionId, documentPath, !!document);
  const subcollections = subcollectionsData?.subcollections ?? [];

  const fieldEntries = document ? getDocumentFieldEntries(document.data) : [];
  const showTimestamps = document ? hasTimestamps(document) : false;

  const handleCopyId = async () => {
    if (!document) return;
    try {
      await navigator.clipboard.writeText(document.id);
      setCopied(true);
      setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handlePeek = (value: unknown, fieldType: PeekFieldType, fieldName: string) => {
    setPeekValue({ value, fieldName, fieldType });
  };

  const handlePeekClose = (open: boolean) => {
    if (!open) setPeekValue(null);
  };

  const handleSubcollectionClick = (subcollectionName: string) => {
    const subcollectionPath = `${documentPath}/${subcollectionName}`;
    const newParams = updateUrlParams(searchParams, {
      connectionId: connectionId ?? undefined,
      collection: subcollectionPath,
      doc: null,
    });
    setSearchParams(newParams);
    onOpenChange(false);
  };

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (peekValue != null) return;
      e.preventDefault();
      onOpenChange(false);
    };
    const doc = globalThis.document;
    doc.addEventListener('keydown', onKeyDown);
    return () => doc.removeEventListener('keydown', onKeyDown);
  }, [open, peekValue, onOpenChange]);

  return {
    document,
    open,
    onOpenChange,
    documentPath,
    copied,
    showTimestamps,
    fieldEntries,
    subcollections,
    peekValue,
    formatFieldValue,
    getSubcollectionDocLabel,
    handleCopyId,
    handlePeek,
    handlePeekClose,
    handleSubcollectionClick,
    onViewJson,
  };
}
